import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../components/Modal';
import { getAssetPath } from '../utils/assetHelper';
import './SettingsSecurityPage.scss';

interface SecurityCard {
  id: string;
  title: string;
  icon: string;
  enabled?: boolean;
  color?: string;
  action?: string;
}

interface LoginHistory {
  id: string;
  device: string;
  location: string;
  ip: string;
  date: string;
  isCurrentDevice: boolean;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SettingsSecurityPage: React.FC = () => {
  // State for security cards
  const [securityCards, setSecurityCards] = useState<SecurityCard[]>([
    { 
      id: '2fa', 
      title: '2FA Status', 
      icon: 'lock-2.svg', 
      enabled: true 
    },
    { 
      id: 'password', 
      title: 'Password', 
      icon: 'security-2.svg', 
      color: 'gray',
      action: 'Change password'
    },
    { 
      id: 'recovery', 
      title: 'Recovery Options', 
      icon: 'recovery.svg', 
      color: 'blue',
      action: 'Manage options'
    }
  ]);

  // State for login history
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([
    { 
      id: '1', 
      device: 'Chrome on Windows', 
      location: 'New York, USA', 
      ip: '192.168.1.1', 
      date: '2023-06-15 14:30', 
      isCurrentDevice: true 
    },
    { 
      id: '2', 
      device: 'Safari on macOS', 
      location: 'London, UK', 
      ip: '192.168.1.2', 
      date: '2023-06-14 09:15', 
      isCurrentDevice: false 
    },
    { 
      id: '3', 
      device: 'Firefox on Ubuntu', 
      location: 'Tokyo, Japan', 
      ip: '192.168.1.3', 
      date: '2023-06-13 22:45', 
      isCurrentDevice: false 
    }
  ]);

  // State for modals
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);

  // State for password form
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Add error state
  const [error, setError] = useState<string | null>(null);

  // Handle 2FA toggle
  const handle2FAToggle = () => {
    setSecurityCards(prev => 
      prev.map(card => 
        card.id === '2fa' ? { ...card, enabled: !card.enabled } : card
      )
    );
    
    // In a real app, this would open a 2FA setup modal
    setIs2FAModalOpen(true);
  };

  // Handle password change click
  const handlePasswordClick = () => {
    setIsPasswordModalOpen(true);
  };

  // Handle recovery options click
  const handleRecoveryClick = () => {
    setIsRecoveryModalOpen(true);
  };

  // Handle password form change
  const handlePasswordFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle password form submit
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit the form to the backend
    console.log('Password form submitted:', passwordForm);
    setIsPasswordModalOpen(false);
    
    // Reset form
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Set document title on component mount
  useEffect(() => {
    document.title = 'Settings - Security | Phantom Clash';
    
    // Initialize tooltips if Bootstrap is available
    if ((window as any).bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function(tooltipTriggerEl) {
        return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  return (
    <>
      {/* Page Menu */}
      <div className="c-page-menu">
        <ul>
          <li><Link to="/settings-profile">Profile</Link></li>
          <li><Link to="/settings-notifications">Notifications</Link></li>
          <li><Link to="/settings-wallet">Wallet</Link></li>
          <li className="active"><Link to="/settings-security">Security</Link></li>
          <li><Link to="/settings-privacy">Privacy</Link></li>
          <li><Link to="/settings-display">Theme & Display</Link></li>
          <li><Link to="/settings-advanced">Advanced Settings</Link></li>
        </ul>
      </div>

      <div className="d-block w-100">
        {/* Error message if any */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        <div className="box position-relative z-1 overflow-hidden mt-2">
          <div className="c-content-box c-content-box--xspadding c-content-box--setting-width">
            <div className="c-content-box__title">
              <h1 style={{ backgroundImage: 'none' }}><i className="bi bi-shield-lock-fill"></i> Security Settings</h1>
              <div className="c-content-box__title-info"></div>
            </div>
            <div className="c-content-box__content">
              <h5 className="c-content-mini-title gray">Protect your account and wallet with additional security options</h5>
              
              {/* Security Cards */}
              <div className="d-flex flex-md-row flex-column align-items-center gap-2">
                {securityCards.map(card => (
                  <div 
                    key={card.id} 
                    className={`c-settings-card ${card.color || ''}`}
                  >
                    {card.id === '2fa' && (
                      <a href="#" className="c-settings-card__hover 2fa" onClick={(e) => { e.preventDefault(); setIs2FAModalOpen(true); }}>
                        <i className="bi bi-three-dots"></i>
                      </a>
                    )}
                    <div className="c-settings-card__icon">
                      <img src={getAssetPath(`assets/images/${card.icon}`)} alt={card.title} />
                    </div>
                    <div className="c-settings-card__text mt-md-0 mt-1">
                      <h4 className="mb-3">{card.title}</h4>
                      {card.enabled !== undefined ? (
                        <label className="c-switch 2fa">
                          <input 
                            type="checkbox" 
                            className="c-switch-input" 
                            checked={card.enabled}
                            onChange={handle2FAToggle}
                          />
                          <span className="c-switch-label" data-on="On" data-off="Off"></span>
                          <span className="c-switch-handle"></span>
                        </label>
                      ) : (
                        <a 
                          href="#" 
                          className={card.id === 'password' ? 'passwordModal' : ''}
                          onClick={(e) => {
                            e.preventDefault();
                            if (card.id === 'password') handlePasswordClick();
                            if (card.id === 'recovery') handleRecoveryClick();
                          }}
                        >
                          {card.action}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Login History */}
              <h3 className="c-content-mini-title-2 mb-4 mt-md-4 mt-3">Login History</h3>
              <div className="c-table-container">
                <table className="c-table">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Location</th>
                      <th>IP Address</th>
                      <th>Date & Time</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map(login => (
                      <tr key={login.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-laptop me-2"></i>
                            {login.device}
                            {login.isCurrentDevice && (
                              <span className="c-badge c-badge--green ms-2">Current</span>
                            )}
                          </div>
                        </td>
                        <td>{login.location}</td>
                        <td>{login.ip}</td>
                        <td>{login.date}</td>
                        <td>
                          {!login.isCurrentDevice && (
                            <a href="#" className="c-table-action" onClick={(e) => e.preventDefault()}>
                              <i className="bi bi-x-circle"></i>
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Security Actions */}
              <div className="d-flex flex-md-row flex-column align-items-center mt-4">
                <a 
                  href="#" 
                  className="c-button c-button--bordered c-button--xl c-button--grayicon mt-4 me-2"
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="bi bi-shield-lock"></i> Change Password
                </a>
                <a 
                  href="#" 
                  className="c-button c-button--bordered c-button--xl c-button--grayicon mt-4"
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="bi bi-door-closed"></i> Log Out All Devices
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Modal */}
      <Modal 
        isOpen={is2FAModalOpen} 
        onClose={() => setIs2FAModalOpen(false)}
        title="Two-Factor Authentication"
        icon={<i className="bi bi-shield-lock"></i>}
      >
        <div className="c-2fa-setup">
          <div className="c-2fa-setup__qr">
            <img src={getAssetPath("assets/images/qr-code-example.png")} alt="QR Code" />
          </div>
          <div className="c-2fa-setup__instructions">
            <h3>Set up Two-Factor Authentication</h3>
            <p>Scan this QR code with your authenticator app (like Google Authenticator, Authy, or Microsoft Authenticator) to set up 2FA.</p>
            <div className="c-input-group mt-4">
              <label htmlFor="authCode" className="c-form-label">Enter Authentication Code</label>
              <input 
                type="text" 
                id="authCode" 
                className="c-form-input" 
                placeholder="Enter the 6-digit code from your app"
              />
            </div>
            <div className="d-flex justify-content-center mt-4">
              <button className="c-button c-button--bordered">Verify & Enable 2FA</button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Password Change Modal */}
      <Modal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        icon={<i className="bi bi-lock"></i>}
      >
        <form onSubmit={handlePasswordSubmit}>
          <div className="c-input-group">
            <label htmlFor="currentPassword" className="c-form-label">Current Password</label>
            <input 
              type="password" 
              id="currentPassword" 
              className="c-form-input" 
              value={passwordForm.currentPassword}
              onChange={handlePasswordFormChange}
              required
            />
          </div>
          <div className="c-input-group">
            <label htmlFor="newPassword" className="c-form-label">New Password</label>
            <input 
              type="password" 
              id="newPassword" 
              className="c-form-input" 
              value={passwordForm.newPassword}
              onChange={handlePasswordFormChange}
              required
            />
          </div>
          <div className="c-input-group">
            <label htmlFor="confirmPassword" className="c-form-label">Confirm New Password</label>
            <input 
              type="password" 
              id="confirmPassword" 
              className="c-form-input" 
              value={passwordForm.confirmPassword}
              onChange={handlePasswordFormChange}
              required
            />
          </div>
          <div className="d-flex justify-content-center mt-4">
            <button type="submit" className="c-button c-button--bordered">Update Password</button>
          </div>
        </form>
      </Modal>

      {/* Recovery Options Modal */}
      <Modal 
        isOpen={isRecoveryModalOpen} 
        onClose={() => setIsRecoveryModalOpen(false)}
        title="Recovery Options"
        icon={<i className="bi bi-shield-check"></i>}
      >
        <div className="c-recovery-options">
          <div className="c-recovery-option">
            <h3><i className="bi bi-envelope"></i> Recovery Email</h3>
            <p>Your recovery email is: <strong>recovery@example.com</strong></p>
            <button className="c-button c-button--sm c-button--bordered mt-2">Change Email</button>
          </div>
          <div className="c-recovery-option mt-4">
            <h3><i className="bi bi-phone"></i> Recovery Phone</h3>
            <p>Your recovery phone is: <strong>+1 (555) 123-4567</strong></p>
            <button className="c-button c-button--sm c-button--bordered mt-2">Change Phone</button>
          </div>
          <div className="c-recovery-option mt-4">
            <h3><i className="bi bi-key"></i> Backup Codes</h3>
            <p>You have 5 unused backup codes.</p>
            <button className="c-button c-button--sm c-button--bordered mt-2">View Codes</button>
            <button className="c-button c-button--sm c-button--bordered mt-2 ms-2">Generate New Codes</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SettingsSecurityPage; 